import { Eye, Thermometer, Scan, Car, Users, Maximize } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

const VIZIOSENSE_CAMERA = "https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/nsofo9y0_VizioSense-camera-removebg-preview.png";

export const ViziosenseSection = () => {
  const { t } = useLanguage();

  const products = [
    { 
      name: t('viziosense.vizioPark'), 
      description: t('viziosense.vizioParkDesc'),
      image: 'https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/dc9xax3u_Vizio%20Park.jpg'
    },
    { 
      name: t('viziosense.vizioYard'), 
      description: t('viziosense.vizioYardDesc'),
      image: 'https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/0259much_Vizio%20Yard.avif'
    },
    { 
      name: t('viziosense.vizioCount'), 
      description: t('viziosense.vizioCountDesc'),
      image: 'https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/67wlim2w_Vizio%20Count.jpg'
    },
    { 
      name: t('viziosense.vizioCrowd'), 
      description: t('viziosense.vizioCrowdDesc'),
      image: 'https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/ndatrxuv_Vizio%20Crowd.jpg'
    },
  ];

  const specs = [
    {
      icon: Eye,
      value: '0.001 Lux',
      label: t('viziosense.sensitivity'),
      description: t('viziosense.sensitivityDesc')
    },
    {
      icon: Thermometer,
      value: '-20°C to 65°C',
      label: t('viziosense.temperature'),
      description: t('viziosense.temperatureDesc')
    },
    {
      icon: Scan,
      value: '105°',
      label: t('viziosense.wideAngle'),
      description: t('viziosense.wideAngleDesc')
    },
  ];

  return (
    <section className="py-20 md:py-32 bg-white" data-testid="viziosense-section">
      <div className="container-0g">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <p className="section-label">{t('viziosense.label')}</p>
          <h2 className="section-title mb-4">
            {t('viziosense.title')}{' '}
            <span className="text-teal-600">{t('viziosense.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            {t('viziosense.description')}
          </p>
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
          {/* Camera Image */}
          <div className="relative flex justify-center">
            <img
              src={VIZIOSENSE_CAMERA}
              alt="Viziosense edge AI camera"
              className="w-full max-w-md h-auto object-contain"
            />
          </div>

          {/* Specs */}
          <div>
            <h3 className="text-2xl font-semibold text-slate-900 mb-6">{t('viziosense.techSpecs')}</h3>
            <div className="space-y-6">
              {specs.map((spec, index) => {
                const Icon = spec.icon;
                return (
                  <div 
                    key={index} 
                    className="flex items-start space-x-4 p-4 bg-slate-50 border border-slate-200 rounded-sm"
                    data-testid={`viziosense-spec-${index}`}
                  >
                    <div className="w-12 h-12 rounded-sm bg-teal-600 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-baseline space-x-2">
                        <span className="text-xl font-bold text-slate-900">{spec.value}</span>
                        <span className="text-sm font-medium text-teal-600">{spec.label}</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{spec.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div>
          <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">{t('viziosense.productRange')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {products.map((product, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-sm border border-slate-200 hover:border-teal-500 transition-colors"
                data-testid={`viziosense-product-${index}`}
              >
                {product.image ? (
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-slate-100 flex items-center justify-center">
                    <span className="text-slate-400">Coming soon</span>
                  </div>
                )}
                <div className="p-4 bg-white">
                  <h4 className="font-semibold text-slate-900">{product.name}</h4>
                  <p className="text-sm text-slate-600">{product.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
